import { useCallback, useEffect, useRef } from 'react';

interface UseAutoSaveOptions {
  onSave: (data: any) => Promise<any>;
  delay?: number;
  enabled?: boolean;
}

export function useAutoSave({ onSave, delay = 500, enabled = true }: UseAutoSaveOptions) {
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isSavingRef = useRef(false);
  const pendingDataRef = useRef<any>(null);

  const debouncedSave = useCallback(
    (data: any) => {
      if (!enabled) return;

      pendingDataRef.current = data;

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = setTimeout(async () => {
        if (isSavingRef.current) return;

        try {
          isSavingRef.current = true;
          await onSave(pendingDataRef.current);
          pendingDataRef.current = null;
        } catch (error) {
          console.error('Auto-save failed:', error);
        } finally {
          isSavingRef.current = false;
        }
      }, delay);
    },
    [onSave, delay, enabled]
  );

  const triggerSave = useCallback(
    (data: any) => {
      debouncedSave(data);
    },
    [debouncedSave]
  );

  const forceSave = useCallback(async () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    if (pendingDataRef.current && !isSavingRef.current) {
      try {
        isSavingRef.current = true;
        await onSave(pendingDataRef.current);
        pendingDataRef.current = null;
      } catch (error) {
        console.error('Force save failed:', error);
        throw error;
      } finally {
        isSavingRef.current = false;
      }
    }
  }, [onSave]);

  const cancelSave = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    pendingDataRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return {
    triggerSave,
    forceSave,
    cancelSave,
    isSaving: isSavingRef.current,
  };
}
